<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AuthenticatedMediaImage from './AuthenticatedMediaImage.vue';
import TelegramMediaAlbum from './TelegramMediaAlbum.vue';

const props = defineProps({
  post: { type: Object, default: null },
  source: { type: Object, default: null },
});

const media = computed(() => props.post?.media || []);
const isPhotoAlbum = computed(
  () =>
    media.value.length > 0 &&
    media.value.length <= 10 &&
    media.value.every(
      item => item.kind === 'image' && Boolean(item.preview_url)
    )
);
const senderName = computed(
  () => props.source?.telegram_name || props.source?.name || 'Telegram-аккаунт'
);
</script>

<template>
  <div>
    <div
      v-if="post"
      class="rounded-xl border border-n-weak bg-n-alpha-2 p-3 sm:p-5"
      data-post-preview="tg_publication_post_preview"
    >
      <div class="mx-auto max-w-md">
        <div class="mb-3 flex min-w-0 items-center gap-3">
          <div class="relative flex-shrink-0">
            <Avatar
              :src="source?.avatar_url || ''"
              :name="senderName"
              :size="40"
              rounded-full
            />
            <span
              class="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-n-solid-1 text-n-brand"
              aria-hidden="true"
            >
              <span class="i-lucide-send size-3" />
            </span>
          </div>
          <div class="min-w-0">
            <p class="m-0 truncate text-sm font-medium text-n-slate-12">
              {{ senderName }}
            </p>
            <p
              v-if="source?.username"
              class="m-0 truncate text-xs text-n-slate-10"
            >
              @{{ source.username }}
            </p>
          </div>
        </div>

        <div
          v-if="isPhotoAlbum"
          class="overflow-hidden rounded-xl rounded-tl-sm border border-n-weak bg-n-solid-1"
        >
          <TelegramMediaAlbum :media="media" />
          <p
            v-if="post.body"
            class="m-0 whitespace-pre-wrap break-words border-t border-n-weak p-3 text-sm leading-5 text-n-slate-12"
          >
            {{ post.body }}
          </p>
        </div>
        <div v-else-if="media.length" class="flex flex-col items-start gap-2">
          <div
            v-for="(item, index) in media"
            :key="item.id"
            class="w-full overflow-hidden rounded-xl rounded-tl-sm border border-n-weak bg-n-solid-1"
          >
            <template v-if="item.kind === 'image' && item.preview_url">
              <AuthenticatedMediaImage
                :src="item.preview_url"
                :alt="item.filename || 'Изображение публикации'"
                class="w-full bg-n-alpha-2"
                image-class="block max-h-[28rem] w-full object-contain"
                placeholder-class="flex min-h-40 flex-col items-center justify-center gap-2 p-4 text-center text-sm text-n-slate-10"
              />
            </template>
            <div
              v-else
              class="flex min-h-24 items-center gap-3 bg-n-alpha-2 p-4"
            >
              <span
                class="size-6 flex-shrink-0 text-n-slate-10"
                :class="
                  item.kind === 'video'
                    ? 'i-lucide-video'
                    : 'i-lucide-file-text'
                "
              />
              <div class="min-w-0">
                <p class="m-0 truncate text-sm font-medium text-n-slate-12">
                  {{ item.filename }}
                </p>
                <p class="m-0 text-xs text-n-slate-10">
                  {{ item.kind === 'video' ? 'Видео' : 'Файл' }}
                </p>
              </div>
            </div>
            <p
              v-if="index === 0"
              class="m-0 whitespace-pre-wrap break-words p-3 text-sm leading-5 text-n-slate-12"
            >
              {{ post.body }}
            </p>
          </div>
        </div>
        <div
          v-else
          class="rounded-xl rounded-tl-sm border border-n-weak bg-n-solid-1 p-3"
        >
          <p
            class="m-0 whitespace-pre-wrap break-words text-sm leading-5 text-n-slate-12"
          >
            {{ post.body }}
          </p>
        </div>

        <p v-if="media.length" class="mb-0 mt-3 text-xs text-n-slate-10">
          <template v-if="isPhotoAlbum">
            Фотографии будут отправлены одним Telegram-альбомом, текст — один
            раз под альбом.
          </template>
          <template v-else>
            Вложения, которые нельзя объединить в фотоальбом, будут отправлены
            по порядку; текст прикрепится к первому.
          </template>
        </p>
      </div>
    </div>
  </div>
</template>
